import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Minus, Plus } from 'lucide-react';
import './BulkQuantityInput.css';

/**
 * BulkQuantityInput Component
 *
 * Row-style quantity stepper with a size label on the left.
 * Used on bulk product detail pages where customers specify quantity per size.
 *
 * Figma: 3796:14022 (parent) — variants:
 *   - Default:                   bg white,     border gray-4
 *   - Hover:                     bg gray-025,  border gray-10
 *   - Focus or Populated Hover:  bg white,     border gray-4 (with value filled)
 *   - Focus & Filled:            bg gray-025,  border gray-10
 *   - Error:                     bg white,     border ui-error, tooltip below
 *
 * Structure (mirrors Figma):
 *   [Label Square (S)]  [value]  ............  [−] [+]
 */
const BulkQuantityInput = ({
  sizeLabel,
  value: controlledValue,
  defaultValue = 0,
  min = 0,
  max = 999,
  step = 1,
  onChange,
  error = '',
  disabled = false,
  className = '',
  id,
  name,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const wrapperRef = useRef(null);

  const setValue = useCallback((newValue) => {
    const clamped = Math.min(max, Math.max(min, newValue));
    if (!isControlled) setInternalValue(clamped);
    onChange?.(clamped);
  }, [min, max, isControlled, onChange]);

  const handleDecrement = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setValue(currentValue - step);
  }, [currentValue, step, disabled, setValue]);

  const handleIncrement = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setValue(currentValue + step);
  }, [currentValue, step, disabled, setValue]);

  const handleKeyDown = useCallback((e) => {
    if (disabled) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
      e.preventDefault();
      setValue(currentValue + step);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setValue(currentValue - step);
    }
  }, [currentValue, step, disabled, setValue]);

  const hasError = !!error;
  const isAtMin = currentValue <= min;
  const isAtMax = currentValue >= max;
  const isPopulated = currentValue > 0;

  const wrapperClasses = [
    'bulk-qty-input',
    isHovered && !isFocused && !hasError && !disabled && 'bulk-qty-input--hover',
    isFocused && 'bulk-qty-input--focus',
    isPopulated && !hasError && 'bulk-qty-input--populated',
    hasError && 'bulk-qty-input--error',
    disabled && 'bulk-qty-input--disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={wrapperRef}
      className={wrapperClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      <div
        className="bulk-qty-input__field"
        role="group"
        aria-label={`Quantity for size ${sizeLabel}`}
        tabIndex={disabled ? undefined : 0}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
      >
        <div className="bulk-qty-input__content">
          {/* Label square (size label) */}
          <div className="bulk-qty-input__label-square">
            <span className="bulk-qty-input__label-text typo-h6">{sizeLabel}</span>
          </div>

          {/* Value */}
          <div className="bulk-qty-input__value-wrap">
            <span className="bulk-qty-input__value typo-input">{currentValue}</span>
            <input type="hidden" id={id} name={name} value={currentValue} />
          </div>

          {/* Stepper buttons */}
          <div className="bulk-qty-input__counts">
            <button
              type="button"
              className={`bulk-qty-input__btn bulk-qty-input__btn--minus${isAtMin ? ' bulk-qty-input__btn--disabled' : ''}`}
              onClick={handleDecrement}
              disabled={disabled || isAtMin}
              tabIndex={-1}
              aria-label="Decrease quantity"
            >
              <Minus size={20} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              className={`bulk-qty-input__btn bulk-qty-input__btn--plus${isAtMax ? ' bulk-qty-input__btn--disabled' : ''}`}
              onClick={handleIncrement}
              disabled={disabled || isAtMax}
              tabIndex={-1}
              aria-label="Increase quantity"
            >
              <Plus size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      {hasError && (
        <div className="bulk-qty-input__error">
          <p className="bulk-qty-input__error-text">{error}</p>
        </div>
      )}
    </div>
  );
};

BulkQuantityInput.propTypes = {
  /** Size label shown in the left square (e.g. "S", "M", "XL") */
  sizeLabel: PropTypes.string.isRequired,
  /** Controlled value */
  value: PropTypes.number,
  /** Initial value (uncontrolled) */
  defaultValue: PropTypes.number,
  /** Minimum value */
  min: PropTypes.number,
  /** Maximum value */
  max: PropTypes.number,
  /** Step increment */
  step: PropTypes.number,
  /** Change handler */
  onChange: PropTypes.func,
  /** Error message (shows tooltip below) */
  error: PropTypes.string,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Additional CSS class */
  className: PropTypes.string,
  /** HTML id for hidden input */
  id: PropTypes.string,
  /** HTML name for hidden input */
  name: PropTypes.string,
};

export default BulkQuantityInput;
